import { Link } from '@inertiajs/react'
import { Fragment } from 'react'
import { CHROME_FOCUS_CLASS } from '../../lib/chrome_config'
import {
  BASEMAP_ATTRIBUTION,
  RELIEF_ATTRIBUTION,
  WINERY_DATA_ATTRIBUTION,
  type AttributionPart,
} from '../../lib/map_config'

interface BasemapAttributionProps {
  className?: string
  testId?: string
  /**
   * The winery layer is drawn, so the ODbL obligation for the winery DATA is
   * live. It is a second obligation, separate from the basemap's own — the
   * basemap credits OpenStreetMap for the tiles under the map, and this credits
   * it for the producers drawn on top of them. One does not discharge the other.
   */
  wineryData?: boolean
  /**
   * Which edge the credits are set against: `start` in running text (the
   * sources page), `end` for the pill in the bottom-right corner of the map.
   * It is a prop and not a `text-right` on the caller's class because it also
   * decides which side of a credit its separator hangs on — see below.
   */
  align?: 'start' | 'end'
  /**
   * A class for the part of a credit that may be left off a narrow row: the
   * relief's list of contributors, after the dataset's name and its link
   * (`AttributionPart.tail`). The map hides it where the row cannot spare the
   * room and shows it from where it can; without one — the sources page — a
   * credit is always whole.
   */
  tailClassName?: string
}

// A CREDIT IS ONE UNBROKEN ITEM, AND THE DOT BETWEEN TWO OF THEM IS DRAWN IN
// THE GAP, NEVER AT THE EDGE OF A LINE.
//
// There are three credits now — the basemap's, the relief's, the winery
// data's — and the rule the two-credit version kept by hand still holds: where
// they do not fit on one line they break BETWEEN credits, into whole
// statements, and no line starts or ends with a separator.
//
// With two credits that was one container query flipping one span from inline
// to block. With three there are three ways to break and the right one depends
// on the widths, so the browser picks: the credits are items of a wrapping
// flex row, and it puts as many on a line as fit. That is one line on a wide
// row, the basemap's and the relief's over the winery data's on a narrower
// one, and each on a line of its own on a phone — where the relief's credit,
// in the wording its dataset asks for, is too long to share a line with either
// of the others.
//
// The separator is each credit's own generated dot, hung OUTSIDE the credit in
// the gap on the side away from the edge the row is set against, and the row
// clips its overflow. Set against the right, a dot hangs to the RIGHT of its
// credit: between two credits that is the gap, and after the last credit of a
// line it is past the row's edge and clipped. Set against the left, the mirror
// image. So a dot shows exactly where two credits share a line, whatever the
// wrap, with no state and no measuring. Generated content, so each credit's
// text is still exactly the credit.
const ROW = 'flex flex-wrap gap-x-[0.9em] overflow-hidden'
const CREDIT = "relative before:absolute before:w-[0.9em] before:text-center before:content-['·']"
const BY_ALIGN = {
  start: { row: `${ROW} justify-start`, credit: `${CREDIT} before:right-full` },
  end: { row: `${ROW} justify-end text-right`, credit: `${CREDIT} before:left-full` },
}

// A credit's link: underlined, and with the chrome's own focus ring. They had
// the browser's 1px blue one, on 9px text, at the bottom corner of a map.
const LINK = `underline rounded-sm ${CHROME_FOCUS_CLASS}`

/**
 * One credit's words, joined by single spaces, each named party linked.
 *
 * A link to somebody else's site opens in a new tab, as it always has: the
 * reader is looking at a map and should not lose it to a licence page. A link
 * to a page of THIS app (the relief's, to its card on the sources page) is an
 * ordinary visit, the same one the header's Sources link makes.
 */
function Parts({ parts, tailClassName }: { parts: readonly AttributionPart[]; tailClassName?: string }) {
  return parts.map((part, index) =>
    // A tail carries its own leading space, so a credit shown without it does
    // not end in one.
    part.tail ? (
      <span key={part.label} data-testid="attribution-tail" className={tailClassName}>
        {' '}
        {part.label}
      </span>
    ) : (
    <Fragment key={part.label}>
      {index > 0 && ' '}
      {!part.href ? (
        part.label
      ) : part.href.startsWith('/') ? (
        <Link href={part.href} className={LINK}>
          {part.label}
        </Link>
      ) : (
        <a href={part.href} target="_blank" rel="noreferrer" className={LINK}>
          {part.label}
        </a>
      )}
    </Fragment>
    ),
  )
}

/**
 * The basemap credit, rendered identically wherever it appears.
 *
 * ODbL requires this visible, so it shows both on the map chrome and on the
 * sources page — a durable record that survives someone redesigning the map.
 * Both surfaces carried their own copy of this markup before, which is one
 * edit away from the two disagreeing about what the licence says.
 *
 * The relief is credited with it, always: the shaded ground is part of the
 * basemap as a reader sees it, drawn from a second open dataset with an
 * attribution of its own (`RELIEF_ATTRIBUTION`, whose wording is the
 * dataset's and whose link leads to the eleven statements it stands for).
 */
export default function BasemapAttribution({
  className,
  testId,
  wineryData,
  align = 'start',
  tailClassName,
}: BasemapAttributionProps) {
  const { row, credit } = BY_ALIGN[align]

  return (
    <p data-testid={testId} className={className}>
      {/* The row is a span inside the paragraph rather than the paragraph
          itself, because the clip has to sit at the edge of the TEXT: the
          caller's class gives the paragraph its padding, and a dot clipped at
          the padding's outer edge would still be drawn inside the pill. */}
      <span className={row}>
        <span className={credit}>
          <Parts parts={BASEMAP_ATTRIBUTION} />
        </span>{' '}
        <span data-testid="relief-attribution" className={credit}>
          <Parts parts={RELIEF_ATTRIBUTION} tailClassName={tailClassName} />
        </span>
        {/* The spaces between the credits are not layout — a flex row drops
            them — they are what keeps the paragraph's text three statements
            rather than one run-on word for anything that reads it as text. */}
        {wineryData && (
          <>
            {' '}
            <span data-testid="winery-attribution" className={credit}>
              Winery data {WINERY_DATA_ATTRIBUTION}
            </span>
          </>
        )}
      </span>
    </p>
  )
}
